import { DatePipe } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ComplaintService } from '../../core/services/complaint.service';
import { OpsService, WorkflowAction, WorkflowItem } from '../../core/services/ops.service';
import { RbacService } from '../../core/services/rbac.service';

type StageState = 'completed' | 'current' | 'pending' | 'skipped';

interface WorkflowStage {
  id: string;
  label: string;
  description: string;
}

const STAGES: WorkflowStage[] = [
  { id: 'submitted', label: 'Submitted', description: 'Grievance registered' },
  { id: 'pending_verification', label: 'Pending verification', description: 'Details awaiting verification' },
  { id: 'under_review', label: 'Under review', description: 'DM desk reviewing grievance' },
  { id: 'routed', label: 'Routed', description: 'Assigned for investigation' },
  { id: 'investigation_completed', label: 'Investigation completed', description: 'Officer report submitted' },
  { id: 'awaiting_hearing', label: 'Awaiting hearing', description: 'Sent to Peshkar for a slot' },
  { id: 'scheduled', label: 'Hearing scheduled', description: 'Hearing date and time fixed' },
  { id: 'heard', label: 'Hearing completed', description: 'DM conducted the hearing' },
  { id: 'resolved', label: 'Resolved', description: 'Final resolution recorded' },
  { id: 'closed', label: 'Closed', description: 'Case formally closed' },
];

const REJECTED_STAGE: WorkflowStage = {
  id: 'rejected',
  label: 'Rejected',
  description: 'Grievance rejected with reason',
};

const REOPENED_STAGE: WorkflowStage = {
  id: 'reopened',
  label: 'Reopened',
  description: 'Closed case returned to active workflow',
};

@Component({
  selector: 'app-workflow',
  standalone: true,
  imports: [FormsModule, DatePipe],
  templateUrl: './workflow.component.html',
  styleUrl: './workflow.component.scss',
})
export class WorkflowComponent {
  private readonly ops = inject(OpsService);
  private readonly rbac = inject(RbacService);
  private readonly complaints = inject(ComplaintService);
  private readonly route = inject(ActivatedRoute);
  readonly queue = this.ops.workflowQueue;
  readonly selectedId = signal<string | number>('');
  readonly queueOpen = signal(false);
  readonly comment = signal('');
  readonly message = signal<string | null>(null);
  readonly role = this.rbac.role;

  readonly selected = computed((): WorkflowItem | undefined => {
    const list = this.queue();
    if (!list.length) return undefined;
    return list.find((i) => String(i.id) === String(this.selectedId())) ?? list[0];
  });

  constructor() {
    this.selectedId.set(this.route.snapshot.queryParamMap.get('case') || '');
  }

  toggleQueue(): void {
    this.queueOpen.update((open) => !open);
  }

  selectCase(id: string | number): void {
    this.selectedId.set(id);
    this.comment.set('');
    this.message.set(null);
    this.queueOpen.set(false);
  }

  statusLabel(status?: string): string {
    return String(status || 'unknown')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  }

  readonly selectedComplaint = computed(() => {
    const item = this.selected();
    if (!item) return undefined;
    return this.complaints.list().find((complaint) => String(complaint.id) === String(item.id));
  });

  readonly currentStageId = computed(() =>
    String(this.selected()?.stage || 'submitted').toLowerCase(),
  );

  readonly stages = computed(() => {
    const current = this.currentStageId();
    if (current === 'rejected') {
      return [...STAGES.slice(0, 4), REJECTED_STAGE];
    }
    if (current === 'reopened') {
      return [...STAGES, REOPENED_STAGE];
    }
    return STAGES;
  });

  readonly progressPercent = computed(() => {
    const current = this.currentStageId();
    const order = this.stages().map((stage) => stage.id);
    const index = order.indexOf(current);
    if (index < 0) return 0;
    return Math.round((index / Math.max(order.length - 1, 1)) * 100);
  });

  readonly allowedActions = computed(() => {
    const item = this.selected();
    if (!item) return [] as WorkflowAction[];
    return this.rbac.filterWorkflowActions(item.allowed);
  });

  async act(action: WorkflowAction): Promise<void> {
    if (!this.rbac.canWorkflowAction(action)) {
      this.message.set(`Your role (${this.role()}) cannot perform “${action}”.`);
      return;
    }
    const item = this.selected();
    if (!item) return;
    try {
      const note = this.comment().trim();
      this.message.set(
        await this.ops.applyWorkflow(item.id, action, note ? { note } : {}),
      );
      this.comment.set('');
    } catch (err) {
      this.message.set(err instanceof Error ? err.message : 'Workflow action failed');
    }
  }

  stageState(stageId: string): StageState {
    const current = this.currentStageId();
    if (stageId === current) return 'current';

    const complaint = this.selectedComplaint();
    const reached = new Set(
      (complaint?.timeline || []).map((event) => String(event.action || '').toLowerCase()),
    );
    reached.add(current);
    if (reached.has(stageId)) return 'completed';

    const order = this.stages().map((stage) => stage.id);
    const currentIdx = order.indexOf(current);
    const stageIdx = order.indexOf(stageId);
    if (currentIdx < 0 || stageIdx < 0 || stageIdx > currentIdx) return 'pending';

    const hearingStage = ['awaiting_hearing', 'scheduled', 'heard'].includes(stageId);
    const usedHearingPath =
      !!complaint?.hearingDate ||
      reached.has('awaiting_hearing') ||
      reached.has('scheduled') ||
      reached.has('heard');
    if (hearingStage && !usedHearingPath && ['resolved', 'closed'].includes(current)) {
      return 'skipped';
    }

    // DB deployments may return only the current version; infer earlier linear stages.
    return 'completed';
  }

  stageMessage(stage: WorkflowStage): string {
    const state = this.stageState(stage.id);
    if (state === 'current') return 'Current stage';
    if (state === 'completed') return 'Completed';
    if (state === 'skipped') return 'Not required';
    return 'Pending';
  }
}
